import { type UploadChangeParam } from "antd/lib/upload";
import { type UploadFile } from "antd/lib/upload/interface";
import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { useCallback, useState } from "react";

import { type IAutofillTempFileData } from "entity/selectionOptions/SelectionOption/SelectionOption.types";

export interface IUseAutofillDocumentUploadResult {
    fileList: UploadFile[];
    uploadedTempFile: IAutofillTempFileData | undefined;
    handleUploadChange: (info: UploadChangeParam) => void;
    handleUploadRemove: () => void;
    reset: () => void;
}

/**
 * Shared document-upload state for the "Autofill from a document" experience. Every returned
 * function is memoized with an empty dependency list — they only ever touch useState setters
 * (already stable) — so consumers can safely put them in their own effect dependency arrays
 * without triggering re-runs on every render.
 */
export function useAutofillDocumentUpload(): IUseAutofillDocumentUploadResult {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploadedTempFile, setUploadedTempFile] = useState<IAutofillTempFileData | undefined>(
        undefined,
    );

    const handleUploadChange = useCallback((info: UploadChangeParam) => {
        setFileList(info.fileList);
        if (info.file.status === "done" && info.file.response) {
            const btFile = info.file.response as BTFileSystem;
            setUploadedTempFile({
                id: btFile.id,
                fileName: btFile.fileName,
                extension: btFile.extension,
                fileSize: btFile.fileSize,
                contentType:
                    info.file.type ?? info.file.originFileObj?.type ?? "application/octet-stream",
            });
        } else if (info.file.status !== "done") {
            setUploadedTempFile(undefined);
        }
    }, []);

    const handleUploadRemove = useCallback(() => {
        setFileList([]);
        setUploadedTempFile(undefined);
    }, []);

    const reset = useCallback(() => {
        setFileList([]);
        setUploadedTempFile(undefined);
    }, []);

    return { fileList, uploadedTempFile, handleUploadChange, handleUploadRemove, reset };
}
